import {
  AdminMembersReportPageParams,
  AdminMembersReportPageStartDate,
} from "@/admin/insights/member-reports/AdminMembersReportPage"
import AdminMembersReportDailyTimeSpentChart from "@/admin/insights/member-reports/tabs/overview/AdminMembersReportDailyTimeSpentChart"
import { AdminMembersReportDailyTimeSpentSectionQuery } from "@/admin/insights/member-reports/tabs/overview/__generated__/AdminMembersReportDailyTimeSpentSectionQuery.graphql"
import { useActiveOrganization } from "@/core/context/ActiveOrganizationContext"
import { useLabels } from "@/core/context/LabelsContext"
import { StateMembershipSessionActivityArea } from "@/core/session/__generated__/MembershipSessionProvider_recordSessionLocationMutation.graphql"
import ProductSelect from "@/product/common/ProductSelect"
import { GlobalID } from "@/relay/RelayTypes"
import Relay from "@/relay/relayUtils"
import makeUseStyles from "@assets/style/util/makeUseStyles"
import { DiscoEmptyState, DiscoSection, DiscoSpinner, DiscoText } from "@disco-ui"
import { useQueryParamState } from "@disco-ui/tabs/DiscoQueryParamTabs"
import DiscoTabs from "@disco-ui/tabs/DiscoTabs"
import { ArrayUtils } from "@utils/array/arrayUtils"
import React, { useEffect, useState } from "react"
import { graphql } from "react-relay"

interface Props {
  filters: { startDate: AdminMembersReportPageStartDate }
}

const AdminMembersReportDailyTimeSpentSection = ({ filters }: Props) => {
  const activeOrganization = useActiveOrganization()!
  const [queryParams] = useQueryParamState<AdminMembersReportPageParams>()
  const [area, setArea] = useState<
    Extract<StateMembershipSessionActivityArea, "community" | "product">
  >(
    activeOrganization.viewerIsOwnerOrAdmin ||
      activeOrganization.viewerMembership?.hasGroupLeadRole
      ? "community"
      : "product"
  )
  const [selectedProductId, setSelectedProductId] = useState<GlobalID | null>(null)

  const classes = useStyles({ area })
  const labels = useLabels()

  const [{ node }] =
    Relay.useRefetchableQuery<AdminMembersReportDailyTimeSpentSectionQuery>(
      graphql`
        query AdminMembersReportDailyTimeSpentSectionQuery(
          $id: ID!
          $inProductsViewerIsAdminOf: Boolean
        ) {
          node(id: $id) {
            __typename
            ... on OrganizationMembership {
              id
              __typename
              productMemberships(
                productType: "course"
                inProductsViewerIsAdminOf: $inProductsViewerIsAdminOf
              ) {
                edges {
                  node {
                    id
                    productId
                  }
                }
              }
            }
          }
        }
      `,
      {
        id: queryParams.om!,
        inProductsViewerIsAdminOf:
          activeOrganization.viewerIsOwnerOrAdmin ||
          activeOrganization.viewerMembership?.hasGroupLeadRole
            ? undefined
            : true,
      },
      { fetchPolicy: "network-only" }
    )

  const organizationMembership = Relay.narrowNodeType(node, "OrganizationMembership")

  useEffect(() => {
    resetState()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryParams.om!])

  if (!organizationMembership) return null

  const productMemberships = Relay.connectionToArray(
    organizationMembership.productMemberships
  )

  return (
    <DiscoSection className={classes.graph}>
      <div className={classes.header}>
        <DiscoText className={classes.title} variant={"body-md-600"}>
          {"Daily Time Spent"}
        </DiscoText>

        {area === "product" && (
          <div className={classes.productSelector}>
            <ProductSelect
              testid={"AdminMembersReportDailyTimeSpentSection.ProductSelect"}
              organizationId={activeOrganization.id}
              value={selectedProductId}
              onChange={(productId) => setSelectedProductId(productId)}
              productIds={productMemberships.map((pm) => pm.productId)}
              type={"course"}
              hideNonPublic={false}
              showStartAdornment
              placeholder={`Please select a ${labels.admin_experience.singular}`}
            />
          </div>
        )}

        {/* We don't care about seeing the tab switcher if they aren't going to be able to select any products */}
        {productMemberships?.length > 0 && (
          <DiscoTabs
            testid={`AdminMembersReportDailyTimeSpentSectionTabs`}
            tabVariant={"grey-track"}
            classes={{ arrowsContainer: classes.tabs }}
            className={classes.tabsContainer}
            routes={[
              ...ArrayUtils.spreadIf(
                {
                  label: `in ${labels.organization.singular}`,
                  onClick: () => {
                    setArea("community")
                    setSelectedProductId(null)
                  },
                  active: area === "community",
                  testid: "community",
                },
                activeOrganization.viewerIsOwnerOrAdmin ||
                  activeOrganization.viewerMembership?.hasGroupLeadRole // Product Admins don't see community level data
              ),
              ...ArrayUtils.spreadIf(
                {
                  label: "in Product",
                  onClick: () => {
                    setArea("product")
                    setSelectedProductId(null)
                  },
                  active: area === "product",
                  testid: "product",
                },
                productMemberships.length > 0
              ),
            ]}
          />
        )}
      </div>

      {area === "community" && (
        <div className={classes.chartSidebarItem}>
          <div className={classes.coloredSquare} />
          <DiscoText
            variant={"body-sm"}
          >{`Time Spent in ${labels.organization.singular}`}</DiscoText>
        </div>
      )}

      {area === "product" && !selectedProductId && (
        <DiscoEmptyState
          testid={"AdminMembersReportDailyTimeSpentSection.product.empty-state"}
          title={`Select a ${labels.admin_experience.singular}`}
          subtitle={`Select a ${labels.admin_experience.singular} to view a ${labels.admin_member.singular}'s activity within it.`}
        />
      )}

      {(area === "community" || (area === "product" && selectedProductId)) && (
        <AdminMembersReportDailyTimeSpentChart
          organizationMembershipId={queryParams.om!}
          filters={{ startDate: filters.startDate, area, selectedProductId }}
        />
      )}
    </DiscoSection>
  )

  function resetState() {
    setSelectedProductId(null)
    setArea(
      activeOrganization.viewerIsOwnerOrAdmin ||
        activeOrganization.viewerMembership?.hasGroupLeadRole
        ? "community"
        : "product"
    )
  }
}

export const AdminMembersReportDailyTimeSpentSectionSkeleton = () => {
  const classes = useStyles({ area: "community" })
  return (
    <DiscoSection className={classes.root} padding={3}>
      <DiscoSpinner absoluteCenter />
    </DiscoSection>
  )
}

type StyleProps = {
  area: Extract<StateMembershipSessionActivityArea, "community" | "product">
}

const useStyles = makeUseStyles((theme) => ({
  root: {
    position: "relative",
    height: 400,
  },
  header: ({ area }: StyleProps) => ({
    display: "grid",
    alignItems: "center",
    gridTemplateColumns: area === "community" ? "130px 1fr" : "130px 1fr auto",
    marginBottom: theme.spacing(2),
    gap: theme.spacing(1),
  }),
  title: {},
  productSelector: {
    width: "380px",
    justifySelf: "flex-end",
    marginLeft: theme.spacing(4),

    [theme.breakpoints.down("md")]: {
      width: "100%",
      marginLeft: 0,
      gridRow: 2,
      gridColumn: "1 / -1",
    },
  },
  graph: {
    height: "380px",
    border: theme.palette.constants.borderSmall,
    boxShadow: theme.palette.groovyDepths.xs,

    [theme.breakpoints.down("md")]: {
      width: "100%",
    },
  },
  tabsContainer: {
    marginBottom: 0,
  },
  tabs: {
    display: "flex",
    alignItems: "center",
    justifySelf: "flex-end",

    "& span": {
      textWrap: "nowrap",
      [theme.breakpoints.down("sm")]: {
        textWrap: "auto",
      },
    },
  },

  chartSidebarItem: {
    display: "flex",
    gap: theme.spacing(1),
    alignItems: "center",
  },
  coloredSquare: {
    width: "16px",
    height: "16px",
    borderRadius: theme.measure.borderRadius.default,
    backgroundColor: "#41D9F1",
  },
}))

export default Relay.withSkeleton({
  component: React.memo(AdminMembersReportDailyTimeSpentSection),
  skeleton: AdminMembersReportDailyTimeSpentSectionSkeleton,
})
